import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MDBBtn, MDBCol } from "mdbreact";
import Spinner from "../../../../components/spinner";
import { currency } from "../../../../services/utilities";
import { getAge } from "../../../../services/utilities";
import { useToasts } from "react-toast-notifications";
import { RemoveSettledAppointment } from "../../../../services/redux/slices/diagnostics/clinic/clinicalBilling";
import {
  ResetBillingDraft,
  SAVE,
  SetCash,
  SetPayment,
  SetPrivilege,
} from "../../../../services/redux/slices/diagnostics/clinic/settlements";
import { HydrateInventoryConsumption } from "../../../../services/redux/slices/diagnostics/clinic/clinicInventory";
import {
  buildSettlementItems,
  getCartSummary,
  getHealthCardCoverage,
  getHealthCardKey,
  getPatientHealthCards,
} from "./utils";

export default function SummaryPanel() {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { token, auth } = useSelector(({ auth }) => auth);
  const { selectedAppointment = null } = useSelector(
    ({ clinicalBilling }) => clinicalBilling,
  );
  const {
    cart = [],
    privilege = 0,
    healthCardContributions = {},
    payment = "cash",
    cash = 0,
    formSubmitted = false,
  } = useSelector(({ settlements }) => settlements);
  const patientHealthCards = getPatientHealthCards(
    selectedAppointment?.patient,
  );
  const { gross, discount, net, vat, vatExempt, lessDiscount } = getCartSummary(
    cart,
    privilege,
  );
  const hasVatExempt = vat > 0;
  const coverage = getHealthCardCoverage({
    cards: patientHealthCards,
    contributions: healthCardContributions,
    net,
  });
  const patientShare = Math.max(Number(net || 0) - coverage, 0);
  useEffect(() => {
    const patient = selectedAppointment?.patient || {};
    const nextPrivilege =
      Number(patient?.privilege || 0) ||
      (getAge(patient?.dob, true) > 59 ? 2 : 0);

    dispatch(SetPrivilege(nextPrivilege));
    // only when the appointment changes, so a manual privilege is kept
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedAppointment?._id]);
  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedAppointment?._id || !selectedAppointment?.patient?._id) return;

    const hasInvalidDispense = cart.some((item) => {
      if (String(item?.itemType || "").toLowerCase() !== "meds") return false;

      const dispensed = (
        Array.isArray(item?.dispenses) ? item.dispenses : []
      ).reduce((sum, row) => sum + Number(row?.qty || 0), 0);

      return dispensed !== Number(item?.qty || 0);
    });

    if (hasInvalidDispense) {
      return addToast(
        "Dispense quantity per batch must match the medicine quantity.",
        { appearance: "error" },
      );
    }

    const settlement = {
      cart: buildSettlementItems(cart, privilege),
      userId: auth._id,
      patient: selectedAppointment.patient._id,
      appointment: selectedAppointment._id,
      consultation: selectedAppointment?.consultation?._id,
      privilege,
      payment,
      sponsorship: (() => {
        let remainingCoverage = coverage;

        return patientHealthCards
          .map((card, index) => {
            const key = getHealthCardKey(card, index);
            const enteredAmount = Number(healthCardContributions[key] || 0);
            const amount = Math.min(enteredAmount, remainingCoverage);
            remainingCoverage -= amount;

            return {
              provider: card?.provider || "healthcard",
              amount,
            };
          })
          .filter((item) => item.provider && item.amount > 0);
      })(),
      discount,
      ...(hasVatExempt && { vat, vatExempt, lessDiscount }),
      cash,
      amount: net,
    };

    dispatch(SAVE({ data: settlement, token })).then((action) => {
      if (!action?.payload?.payload?.appointment?._id) return;

      dispatch(
        HydrateInventoryConsumption({
          clinicId:
            selectedAppointment?.clinicId || selectedAppointment?.clinic?._id,
          settlementId: action?.payload?.payload?._id,
          cart,
        }),
      );

      dispatch(
        RemoveSettledAppointment(action.payload.payload.appointment._id),
      );
      dispatch(ResetBillingDraft());
    });
  };

  return (
    <MDBCol md="3" className="mb-4">
      <div className="border rounded p-3 h-100 bg-white">
        <form onSubmit={handleSubmit}>
          <table className="summary-table w-100">
            <thead>
              <tr>
                <th colSpan="2" className="th-custom">
                  Summary
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Gross Amount</td>
                <td className="table-price text-right">
                  {currency.format(gross)}
                </td>
              </tr>
              <tr>
                <td>{hasVatExempt ? "Discount 20%" : "Discount"}</td>
                <td className="table-price text-right">
                  {currency.format(hasVatExempt ? lessDiscount : discount)}
                </td>
              </tr>
              {hasVatExempt && (
                // VAT is added back, the patient pays it (same as the invoice)
                <tr>
                  <td>VAT 12%</td>
                  <td className="table-price text-right">
                    {currency.format(vat)}
                  </td>
                </tr>
              )}
              <tr>
                <td>Coverage</td>
                <td className="table-price text-right">
                  {currency.format(coverage)}
                </td>
              </tr>
              <tr>
                <td>Net Amount</td>
                <td className="table-price text-right">
                  {currency.format(patientShare)}
                </td>
              </tr>
              {patientShare > 0 && (
                <>
                  <tr>
                    <td>Payment</td>
                    <td className="p-0">
                      <select
                        value={payment}
                        onChange={({ target }) =>
                          dispatch(SetPayment(target.value))
                        }
                        className="form-control"
                      >
                        {["cash", "gcash"].map((item) => (
                          <option key={item} value={item}>
                            {item.toUpperCase()}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="2">
                      {payment === "cash" ? (
                        <input
                          type="number"
                          min={patientShare || 0}
                          value={String(cash)}
                          onChange={({ target }) =>
                            dispatch(SetCash(Number(target.value)))
                          }
                          placeholder="Amount in Peso"
                          required
                          name="amount"
                          className="form-control"
                        />
                      ) : (
                        <div className="text-muted small">
                          No cash input needed for this payment type.
                        </div>
                      )}
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>

          <MDBBtn
            type="submit"
            className="m-0 w-100 fw-bold mt-3"
            disabled={
              !selectedAppointment || cart.length === 0 || formSubmitted
            }
            color="success"
          >
            Complete Billing <Spinner formSubmitted={formSubmitted} />
          </MDBBtn>
        </form>
      </div>
    </MDBCol>
  );
}
